import Notification from '../../core/Notification';
import Locked from '../../core/Locked';
import VersionWarning from '../../core/VersionWarning';
import ProjectProvider from '../../core/providers/ProjectProvider';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Component, default as React } from 'react';
import Errored from '../../core/Errored';
import Loading from '../../core/Loading';
import { getGraphFromProject, makeCancelable, sortObjects } from '../../core/utils/utils';
import withGS from '../../core/withGShoc';
import GS from '../../grafsync/grafsync';

const loadOptions = {};

class DataProjectInfoContainer extends Component {

  constructor(props) {
    super(props);
    this.state = {
      initialLoad: true,
      isLoading: true,
      infoLoading: true,
      datasets: [],
      projectUsers: undefined,
      ontologyProjects: [],
      isErrored: undefined,
      entitlements: undefined
    };
    this.cancellable = null;
  }

  static getDerivedStateFromProps(props, state) {
    if (props.data._version !== state._version) {
      return {
        infoLoading: true,
        isLoading: false,
        _version: props.data._version,
      };
    }
    return null;
  }

  componentDidUpdate() {
    if (!this.state.isErrored && this.state.infoLoading === true) {
      this.collectProjectData();
    }
  }

  componentDidMount() {
    this.collectProjectData();
  }

  loadData(iris, options) {
    this.cancellable = makeCancelable(GS.loadData(iris, options, this.props.graph, false));
    return this.cancellable;
  }

  loadModel(graph) {
    this.cancellable = makeCancelable(GS.loadModel(graph));
    return this.cancellable;
  }

  loadEntitlements() {    
    this.cancellable = makeCancelable(GS.getProjectEntitlements(this.props.user, this.props.project));
    return this.cancellable;
  }

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
  }

  doGetUsers() {
    this.cancellable = makeCancelable(GS.subscriptionUsers(this.props.id));
    return this.cancellable;
  }

  collectProjectData() {
    if (this.state.isLoading) return;
    // At some point we want to filter the properties loaded for each component
    this.setState({ isLoading: true }, async () => {
      try {
        // We only load these once - they won't change very often at all
        if (this.state.entitlements === undefined && !this.props.omitEntitlements) {
          const entitlements = await this.loadEntitlements().promise;
          if (this.cancellable && this.cancellable.isCancelled()) return;
          const users = await this.doGetUsers().promise;
          if (this.cancellable && this.cancellable.isCancelled()) return;
          const projectUsers = users.map(user => ({
            value: user.id,
            label: user.givenName && user.familyName ?
              user.givenName + " " + user.familyName
              :
              user.username
          })).sort(function (a, b) {
            return a.label.toLowerCase() < b.label.toLowerCase() ? -1 : 1;
          });
          this.setState({ entitlements: entitlements.hasEntitlement, projectUsers });
        }
        if (this.props.data.hasComponent || this.props.data.ontologyProjectAssociation) {
          const opa = this.props.data.ontologyProjectAssociation ? this.props.data.ontologyProjectAssociation : [];
          const cpt = this.props.data.hasComponent ? this.props.data.hasComponent : [];
          const resources = [...opa, ...cpt];
          const mergedLoadOptions = Object.assign({}, loadOptions);
          if (resources.length > 0) {
            await this.loadData(resources, mergedLoadOptions).promise;
            if (this.cancellable && this.cancellable.isCancelled()) return;
          }
          await this.processProjects();
          if (this.cancellable && this.cancellable.isCancelled()) return;
          this.processComponents();
        }
      }
      catch (e) {
        if (this.cancellable && this.cancellable.isCancelled()) return;
        this.setState({ isErrored: e });
      }
      this.setState({ isLoading: false, infoLoading: false, initialLoad: false });
    });
  }

  async processProjects() {
    const { ontologyProjectAssociation } = this.props.data;
    if (ontologyProjectAssociation) {
      let ontologyProjects = [];
      const fetchModels = ontologyProjectAssociation.map(opa => {
        const opaData = GS.data(opa, this.props.graph);
        ontologyProjects.push(opaData.associatedProject);
        // We load models so we see updates to it
        return this.loadModel(getGraphFromProject(opaData.associatedProject)).promise;
      });
      await Promise.all(fetchModels);
      if (this.cancellable && this.cancellable.isCancelled()) return;
      this.setState({ ontologyProjects });
    }
  }

  processComponents() {
    try {
      if (this.props.data.hasComponent) {
        let datasets = [];
        this.props.data.hasComponent.forEach(component => {
          const componentData = GS.data(component, this.props.graph, false);
          if (componentData) {
            if (componentData.type.includes("Dataset")) {
              datasets.push({
                id: componentData.id,
                title: componentData.title,
                type: componentData.type,
                useWorkflow: componentData.activateWorkflow === true,
                description: componentData.description
              });
            }
          }
        });
        sortObjects(datasets, 'title', this.props.data.defaultLang, "und", false);
        this.setState({ datasets });
      }
    }
    catch (e) {
      if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
    }
  }

  addComponent = async (title, componentIRI, isSlashIRI, description, lang = "en", isOrdered, isUuidIRI, useXL = false, counters) => {
    let newComponent = {
      id: componentIRI,
      type: "Dataset",
      title: { [lang]: title },
      useSlashIRI: isSlashIRI,
      useUuidIRI: isUuidIRI,
      ...(counters) && {
        useCounterIRI : true,
        primaryCounter: counters.primaryCounter
      },
      minimiseEncoding: true,
      ...(description && description !== "") && { description: { [lang]: description } }
    };
    await GS.insertResource(newComponent, componentIRI, this.props.graph);
    GS.addProperty(this.props.project, this.props.graph, 'hasComponent', componentIRI);
  };

  deleteComponent = (iri, link) => {
    GS.deleteResource(iri, this.props.graph);
    this.props.history.push(link);
  };

  monitorLinkUpdate = (iri, property) => {
    if (property === "title") this.processComponents();
  };

  render() {

    const { id, data, passthroughComponent, graph, lockedGraphs, projectType, omitEntitlements } = this.props;
    const { isLoading, isErrored, datasets, ontologyProjects, entitlements, initialLoad, projectUsers } = this.state;

    if (lockedGraphs && ontologyProjects && lockedGraphs.length > 0 && ontologyProjects.length > 0) {
      const shouldLock = lockedGraphs.some(lg => ontologyProjects.some(op => getGraphFromProject(op) === lg));
      if (shouldLock) return <Locked />;
    }

    if (data.isVersionOfProject) return <VersionWarning />;
    if (projectType && projectType !== data.type) return <Notification spaceRight={true} text="You are trying to view an incorrect project type." />;
    if (data.pendingDeletion) return <Notification spaceRight={true} dataTest="pending-deletion" text="This project is pending deletion." />;
    if (isErrored) return <Errored dataTest="datasetcontainer-problem" data={isErrored} />;
    if (isLoading && initialLoad) return <Loading offset="40px" size="large" message="Loading project data..." />;
    if (!entitlements && !omitEntitlements) return null;

    const WrappedComponent = passthroughComponent;

    return (
      <ProjectProvider value={data}>
        <WrappedComponent
          {...this.props}
          id={id}
          graph={graph}
          project={id}
          data={data}
          projectUsers={projectUsers}
          entitlements={entitlements}
          datasets={datasets}
          xrefComponents={datasets}
          ontologyProjects={ontologyProjects}
          // The main components
          components={datasets}
          // All components (same as above for data projects)
          existingComponents={[].concat(datasets)}
          addComponent={this.addComponent}
          onDeleteComponent={this.deleteComponent}
          monitorLinkUpdate={this.monitorLinkUpdate}
        />
      </ProjectProvider>
    );
  }
}

DataProjectInfoContainer.propTypes = {
  omitEntitlements: false
};

DataProjectInfoContainer.propTypes = {
  data: PropTypes.object.isRequired,
  id: PropTypes.string.isRequired,
  history: PropTypes.object.isRequired,
  project: PropTypes.string.isRequired,
  projectType: PropTypes.string,
  lockedGraphs: PropTypes.array,
  user: PropTypes.object.isRequired,
  graph: PropTypes.string.isRequired
};

export default withRouter(withGS(DataProjectInfoContainer));